import { useCallback, useRef } from 'react'

interface UseLongPressOptions {
  onLongPress: (e: PointerEvent) => void
  ms?: number
}

interface UseLongPressResult {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
  onPointerLeave: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerCancel: (e: React.PointerEvent) => void
}

export function useLongPress({ onLongPress, ms = 450 }: UseLongPressOptions): UseLongPressResult {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startX = useRef(0)
  const startY = useRef(0)
  const fired = useRef(false)

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    fired.current = false
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      startX.current = e.clientX
      startY.current = e.clientY
      fired.current = false

      const nativeEvent = e.nativeEvent
      timerRef.current = setTimeout(() => {
        fired.current = true
        navigator.vibrate?.(10)
        onLongPress(nativeEvent)
        timerRef.current = null
      }, ms)
    },
    [onLongPress, ms]
  )

  const onPointerUp = useCallback(
    (_e: React.PointerEvent) => {
      cancel()
    },
    [cancel]
  )

  const onPointerLeave = useCallback(
    (_e: React.PointerEvent) => {
      cancel()
    },
    [cancel]
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const dx = e.clientX - startX.current
      const dy = e.clientY - startY.current
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        cancel()
      }
    },
    [cancel]
  )

  const onPointerCancel = useCallback(
    (_e: React.PointerEvent) => {
      cancel()
    },
    [cancel]
  )

  return { onPointerDown, onPointerUp, onPointerLeave, onPointerMove, onPointerCancel }
}
